import { Link } from 'react-router-dom';

const FOOTER_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About Us', path: '/about' },
  { label: 'Tampa Bay', path: '/location' },
];

const SERVICES = [
  'Photometric Lighting Design',
  'Commercial LED Upgrades',
  'Sports & Court Lighting',
  'Car Dealership Lighting',
  'Industrial & High Bay',
  'Solar All-In-One Systems',
];

export default function Footer() {
  return (
    <footer className="bg-white border-t border-gray-100">
      {/* Top accent line */}
      <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-40" />

      <div className="mx-auto px-6 lg:px-12 xl:px-20 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-7">
              <img
                src="https://www.selightingdesign.com/wp-content/uploads/2022/01/Southeast-Lighting-Design-Logo-cropped-scaled.jpg"
                alt="Southeast Lighting & Design"
                className="h-11 w-auto object-contain"
              />
            </Link>
            <p className="text-charcoal/55 text-sm leading-[1.85] max-w-xs">
              Tampa Bay&apos;s full-service commercial LED lighting firm — photometric design, product specification, installation coordination, and rebate acquisition managed under one roof.
            </p>
            <div className="flex items-center gap-3 mt-8">
              <a
                href="https://www.facebook.com/ArkonPower/?ref=br_rs"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-charcoal/15 text-charcoal/50 hover:text-gold hover:border-gold transition-all duration-300 cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-sm"></i>
              </a>
              <a
                href="mailto:Info@SELightingDesign.com"
                className="w-9 h-9 flex items-center justify-center border border-charcoal/15 text-charcoal/50 hover:text-gold hover:border-gold transition-all duration-300 cursor-pointer"
                aria-label="Email"
              >
                <i className="ri-mail-line text-sm"></i>
              </a>
            </div>
          </div>

          {/* Spacer */}
          <div className="hidden lg:block lg:col-span-1" />

          {/* Navigation */}
          <div className="lg:col-span-2">
            <h4 className="text-[9px] font-bold tracking-[0.35em] uppercase text-gold mb-6">
              Navigation
            </h4>
            <ul className="space-y-3.5">
              {FOOTER_LINKS.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-charcoal/55 text-sm hover:text-charcoal transition-colors duration-300 cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-[9px] font-bold tracking-[0.35em] uppercase text-gold mb-6">
              Services
            </h4>
            <ul className="space-y-3.5">
              {SERVICES.map((s) => (
                <li key={s}>
                  <Link
                    to="/services"
                    className="text-charcoal/55 text-sm hover:text-charcoal transition-colors duration-300 cursor-pointer"
                  >
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2">
            <h4 className="text-[9px] font-bold tracking-[0.35em] uppercase text-gold mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:7279149002"
                  className="flex items-center gap-3 group cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-gold text-sm"></i>
                  </div>
                  <span className="text-charcoal/55 text-sm group-hover:text-charcoal transition-colors whitespace-nowrap">
                    727-914-9002
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:Info@SELightingDesign.com"
                  className="flex items-start gap-3 group cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-gold text-sm"></i>
                  </div>
                  <span className="text-charcoal/55 text-xs group-hover:text-charcoal transition-colors leading-relaxed">
                    Info@SELightingDesign.com
                  </span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-gold text-sm"></i>
                </div>
                <span className="text-charcoal/55 text-sm leading-relaxed">
                  Tampa Bay Area, FL
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-100 pt-8 space-y-3">
          {/* Brand copyright row */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-charcoal/40 text-xs tracking-wide">
              &copy; {new Date().getFullYear()} Southeast Lighting &amp; Design. All Rights Reserved.
            </p>
            <div className="flex items-center gap-1 text-charcoal/30 text-xs tracking-wider">
              <span>Commercial LED Lighting</span>
              <span className="text-gold/50">·</span>
              <span>Tampa Bay, Florida</span>
            </div>
          </div>
          {/* Legal entity — visually separated */}
          <div className="border-t border-gray-50 pt-3">
            <p className="text-charcoal/25 text-[10px] tracking-widest text-center sm:text-left">
              A Southeast Green Energy Solutions, LLC Company
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
